/**
 * Canonical MessageInstanceEventClient implementation.
 *
 * This is the source of truth for frontend in-app message instance events.
 * `@atlassian/message-instance-event-frontend-client/message-instance-event-client` and the legacy
 * `@atlassian/message-instance-event-frontend-client` exports this implementation.
 */

import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { NarrowExposureCohort } from './narrow-exposure/types';

export type AdditionalMetadata = Record<string, string | number | boolean | null> & {
	transactionAccount?: string;
	tenantId?: string;
};

type InAppPullContext = {
	// PostOfficeContextValue fields
	product?: string;
	locale?: string;
	tenantId?: string;
	orgId?: string;
	subproduct?: string;
	isSiteAdmin?: boolean;
	workspaceId?: string;
	// Required for pull context
	source: string;
	recommendation: {
		recommendationSessionId: string | null;
		recommendationEntityId: string | null;
		itemFeatureSetId: string | null;
	};
};

type InAppMessageInstanceEventBase = {
	messageInstanceId: string;
	messageTemplateId: string;
	deliveryChannel: 'in-app';
	placementId: string;
	channelType: 'pull';
	context: InAppPullContext;
	messageCreationType: 'implicit' | 'explicit';
	messageTriggerId: string;
	journeyId?: string;
	journeyTriggerId?: string;
	variant?: string | number | boolean | null;
	configSetId?: string;
	origin?: {
		originId: string;
		originProduct: string;
	};
	timestamp: number;
};

export type ClickedElementAttributes =
	| ClickedElementAttributesButton
	| ClickedElementAttributesOther
	| ClickedElementAttributesNotApplicable;

export type ClickedElementAttributesButton = {
	/** A human-readable camel-cased string to identify the click, for example: learnMore, tryItNow. */
	clickIdentifier: string;
	clickedElement: 'button';
	buttonType: 'primary' | 'secondary';
};

export type ClickedElementAttributesOther = {
	/** A human-readable camel-cased string to identify the click, for example: learnMore, tryItNow. */
	clickIdentifier: string;
	clickedElement: 'link' | 'dropdown-menu' | 'cross-icon';
};

// Never exported. Nothing outside this file can reference this symbol, so nothing outside
// this file can construct a value typed `ClickedElementAttributesNotApplicable` other than
// via `notApplicableClickedElement` below.
declare const NOT_APPLICABLE_BRAND: unique symbol;

/**
 * `not-applicable` is emitted by the semantic interaction contract for kinds whose meaning
 * does not depend on a rendered element (`declined`, `closed`, `snoozed`, `stepChanged`). It
 * exists only for backward compatibility while `clickedElement` remains required, and is
 * removed once consumers read `action.actionType` alone.
 *
 * Branded so that Message Makers cannot author this value themselves — see
 * `notApplicableClickedElement`.
 */
export type ClickedElementAttributesNotApplicable = {
	clickIdentifier: string;
	clickedElement: 'not-applicable';
	readonly [NOT_APPLICABLE_BRAND]: true;
};

/**
 * The only supported way to produce a `ClickedElementAttributesNotApplicable`. Reserved for
 * the semantic interaction contract's own mapping — Message Makers must not call this.
 */
export const notApplicableClickedElement = (
	clickIdentifier: string,
): ClickedElementAttributesNotApplicable =>
	({ clickIdentifier, clickedElement: 'not-applicable' }) as ClickedElementAttributesNotApplicable;

export type ActionTypeAttributes =
	| ActionTypeAttributesOpen
	| ActionTypeAttributesOther
	| ActionTypeAttributesSnooze
	| ActionTypeAttributesSteps
	| ActionTypeAttributesRead
	| ActionTypeAttributesUnread;

export type ActionTypeAttributesOpen = {
	actionType: 'open';
	destinationUrl?: string;
	trackingLinkId?: string;
};

export type ActionTypeAttributesSnooze = {
	actionType: 'snooze';
	snoozeDuration: number;
};

export type ActionTypeAttributesSteps = {
	actionType: 'next' | 'previous';
	currentStep: number;
	previousStep: number;
	stepName?: string;
};

export type ActionTypeAttributesOther = {
	actionType: 'acknowledge' | 'dismiss' | 'react';
};

export type ActionTypeAttributesRead = {
	actionType: 'read';
};

export type ActionTypeAttributesUnread = {
	actionType: 'unread';
};

export type InAppViewedEvent = InAppMessageInstanceEventBase & {
	messageEventType: 'viewed';
	additionalMetadataFromFrontend?: AdditionalMetadata | null;
};

export type InAppDeliverySuccessfulEvent = InAppMessageInstanceEventBase & {
	messageEventType: 'deliverySuccessful';
	additionalMetadataFromFrontend?: AdditionalMetadata | null;
};

export type InAppClickedEvent = InAppMessageInstanceEventBase & {
	messageEventType: 'clicked';
	/**
	 * Identifies which control the user interacted with, for events produced by the
	 * semantic interaction contract. Optional while legacy events share this endpoint.
	 */
	interactionIdentifier?: string;
	clickedElement: ClickedElementAttributes;
	action: ActionTypeAttributes;
	additionalMetadataFromFrontend?: AdditionalMetadata | null;
};

/** Why the frontend declined to show a message. */
export type MessageInstanceDropDetails = {
	dropReason: 'narrowExposureExperiment';
	narrowExposureExperiment: {
		statsigExperiment: string;
		statsigParameterName: string;
		/** The user's cohort, or `unevaluated` when Statsig returned no usable cohort. */
		cohort: NarrowExposureCohort;
	};
};

export type InAppDroppedEvent = InAppMessageInstanceEventBase &
	MessageInstanceDropDetails & {
		messageEventType: 'dropped';
		/** B3 trace id of the placement response that produced the message. */
		placementTraceId?: string;
		additionalMetadataFromFrontend?: AdditionalMetadata | null;
	};

export type FrontendMessageInstanceEventWithoutRecipient =
	| InAppViewedEvent
	| InAppDeliverySuccessfulEvent
	| InAppClickedEvent
	| InAppDroppedEvent;

export type MessageInstanceClickedParams = Omit<
	InAppClickedEvent,
	keyof Omit<InAppMessageInstanceEventBase, 'additionalMetadataFromFrontend' | 'messageEventType'>
> & {
	messageInstanceId?: string;
	messageTemplateId?: string;
	messageTriggerId?: string;
	messageCreationType?: 'implicit' | 'explicit';
	additionalMetadataFromFrontend?: AdditionalMetadata;
};

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

const DEFAULT_PATH = `/gateway/api/post-office/api/v1/message-instance-event/bulk`;
const FLUSH_BATCH_EVENTS_TIMEOUT = 1000;
const MESSAGE_EVENTS_BATCH_SIZE = 30;
const CSAT_COOLDOWN_MAX_ATTEMPTS = 3;
const CSAT_COOLDOWN_RETRY_WINDOW_MS = 30_000;
const CSAT_COOLDOWN_REQUEST_TIMEOUT_MS = 5_000;

const retryAfterMilliseconds = (response: Response | undefined): number => {
	const value = response?.headers.get('Retry-After');
	if (!value) {
		return 0;
	}
	const delay = /^\d+$/.test(value) ? Number(value) * 1000 : Date.parse(value) - Date.now();
	return Number.isNaN(delay) ? 0 : Math.max(0, delay);
};

// Retry only the CSAT display cooldown, not viewed events or other templates' interactions.
// Replays may extend expiry and duplicate cooldown analytics if an earlier attempt was accepted.
// Keep the window short and never treat HTTP acceptance as proof of durable persistence.
const sendCsatCooldown = async (endpointUrl: string, body: string): Promise<void> => {
	const deadline = Date.now() + CSAT_COOLDOWN_RETRY_WINDOW_MS;
	for (let attempt = 0; attempt < CSAT_COOLDOWN_MAX_ATTEMPTS; attempt++) {
		const remaining = deadline - Date.now();
		if (remaining <= 0) {
			break;
		}
		const controller = new AbortController();
		const timeout = globalThis.setTimeout(
			() => controller.abort(),
			Math.min(CSAT_COOLDOWN_REQUEST_TIMEOUT_MS, remaining),
		);
		let response: Response | undefined;
		try {
			response = await fetch(endpointUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body,
				keepalive: true,
				signal: controller.signal,
			});
		} catch (error) {
			if (
				!(error instanceof TypeError) &&
				!(error instanceof DOMException && error.name === 'AbortError')
			) {
				throw error;
			}
		} finally {
			globalThis.clearTimeout(timeout);
		}
		if (response?.ok) {
			return;
		}
		if (response && ![408, 429, 500, 502, 503, 504].includes(response.status)) {
			break;
		}
		if (attempt === CSAT_COOLDOWN_MAX_ATTEMPTS - 1) {
			break;
		}
		const delay = Math.max(
			1000 * 2 ** attempt + Math.floor(Math.random() * 250),
			retryAfterMilliseconds(response),
		);
		// Do not shorten Retry-After or replay a stale event after a suspended tab resumes.
		if (Date.now() + delay >= deadline) {
			break;
		}
		await new Promise<void>((resolve) => globalThis.setTimeout(resolve, delay));
	}
	throw new Error('Post Office CSAT display cooldown delivery failed');
};

if (typeof fetch === 'undefined') {
	// If fetch is not supported, load the polyfill.
	require('whatwg-fetch');
}

export class MessageInstanceEventClient {
	private readonly endpointUrl: string = DEFAULT_PATH;
	private scheduledTimeout: number | null = null;
	private batchEvents: FrontendMessageInstanceEventWithoutRecipient[] = [];

	public async sendMessageInstanceEvent(
		event: FrontendMessageInstanceEventWithoutRecipient,
	): Promise<void> {
		if (
			event.messageEventType === 'clicked' &&
			event.interactionIdentifier === 'messageDisplayedCooldown' &&
			event.action.actionType === 'snooze'
		) {
			if (event.messageTemplateId === 'rovo-chat-csat-survey') {
				return sendCsatCooldown(this.endpointUrl, JSON.stringify([event]));
			}
			// Display cooldowns must not wait behind (or be lost by) the analytics batch.
			// Keep this path separate: other lifecycle events retain their existing delivery.
			const response = await fetch(this.endpointUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify([event]),
				keepalive: true,
			});
			if (!response.ok) {
				throw new Error(`Post Office display cooldown request failed: ${response.status}`);
			}
			// Other templates retain their single-attempt delivery contract.
			return;
		}

		this.batchEvents.push(event);
		if (this.scheduledTimeout === null) {
			this.scheduledTimeout = globalThis.window.setTimeout(
				this.flushBatchEvents.bind(this),
				FLUSH_BATCH_EVENTS_TIMEOUT,
			);
		}
	}

	private async sendEventsToBackend(events: FrontendMessageInstanceEventWithoutRecipient[]) {
		const url = `${this.endpointUrl}`;
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(events),
		});
		if (!response.ok) {
			// eslint-disable-next-line no-console
			console.error(`Failed to send event to analytics endpoint: ${response.statusText}`);
		}
	}

	private async flushBatchEvents() {
		if (this.batchEvents.length === 0) {
			return;
		}

		if (fg('post-office_enable_safe-event-batching')) {
			return this.flushBatchEventsSafely();
		}

		if (this.batchEvents.length > MESSAGE_EVENTS_BATCH_SIZE) {
			const batch = this.batchEvents.slice(0, MESSAGE_EVENTS_BATCH_SIZE);
			await this.sendEventsToBackend(batch);
			if (this.scheduledTimeout !== null) {
				globalThis.window.clearTimeout(this.scheduledTimeout);
			}
			this.batchEvents = this.batchEvents.slice(MESSAGE_EVENTS_BATCH_SIZE, this.batchEvents.length);
			this.scheduledTimeout = globalThis.window.setTimeout(
				this.flushBatchEvents.bind(this),
				FLUSH_BATCH_EVENTS_TIMEOUT,
			);
			return;
		}

		await this.sendEventsToBackend(this.batchEvents);
		this.batchEvents = [];

		if (this.scheduledTimeout !== null) {
			globalThis.window.clearTimeout(this.scheduledTimeout);
			this.scheduledTimeout = null;
		}
	}

	private async flushBatchEventsSafely() {
		// Detach the attempted batch so events arriving during fetch remain queued.
		const batch = this.batchEvents.splice(0, MESSAGE_EVENTS_BATCH_SIZE);
		try {
			await this.sendEventsToBackend(batch);
		} catch {
			// eslint-disable-next-line no-console
			console.error('Failed to send event to analytics endpoint');
		} finally {
			if (this.scheduledTimeout !== null) {
				globalThis.window.clearTimeout(this.scheduledTimeout);
			}
			this.scheduledTimeout =
				this.batchEvents.length > 0
					? globalThis.window.setTimeout(
							this.flushBatchEvents.bind(this),
							FLUSH_BATCH_EVENTS_TIMEOUT,
						)
					: null;
		}
	}
}

declare global {
	interface Window {
		postOfficeMessageInstanceEventClient?: MessageInstanceEventClient;
	}
}
